import { col, type Shape } from './base.ts';

export const PTS_RATES_PTS1: Shape = [
  col('oilRate', 'liquidRate', 'Well-test oil rate at line conditions.', { suffix: 'wellTestOilRatePts1' }),
  col('gasRate', 'gasRate', 'Well-test gas rate at line conditions.', { suffix: 'wellTestGasRatePts1' }),
  col('waterRate', 'liquidRate', 'Well-test water rate at line conditions.', { suffix: 'wellTestWaterRatePts1' }),
  col('liquidRate', 'liquidRate', 'Well-test liquid rate at line conditions.', { suffix: 'wellTestLiquidRatePts1' }),
  col('waterCut', 'fraction', 'Well-test water cut at line conditions.', { suffix: 'wellTestWaterCutPts1' }),
  col('gor', 'gasOilRatio', 'Well-test gas-oil ratio at line conditions.', { suffix: 'wellTestGORPts1' }),
  col('wgr', 'liquidGasRatio', 'Well-test water-gas ratio at line conditions.', { suffix: 'wellTestWGRPts1' }),
  col('solutionGor', 'gasOilRatio', 'Solution gas-oil ratio at line conditions.', { suffix: 'solutionGORPts1' }),
  col('solutionCgr', 'liquidGasRatio', 'Solution condensate-gas ratio at line conditions.', {
    suffix: 'solutionCGRPts1',
  }),
];

/**
 * PTS well-test rates at STANDARD conditions (the `Pts2` sample) on
 * `PtsOutputs_String_Agent`. Trailing `…Pts2` marker absorbed into each
 * `suffix`; applied with the default empty prefix. The GOR column is spelled
 * `wellTestGorPts2` (mixed-case `Gor`) here, unlike the `Pts1` sample.
 */
export const PTS_RATES_PTS2: Shape = [
  col('oilRate', 'liquidRate', 'Well-test oil rate at standard conditions.', { suffix: 'wellTestOilRatePts2' }),
  col('gasRate', 'gasRate', 'Well-test gas rate at standard conditions.', { suffix: 'wellTestGasRatePts2' }),
  col('waterRate', 'liquidRate', 'Well-test water rate at standard conditions.', { suffix: 'wellTestWaterRatePts2' }),
  col('liquidRate', 'liquidRate', 'Well-test liquid rate at standard conditions.', {
    suffix: 'wellTestLiquidRatePts2',
  }),
  col('waterCut', 'fraction', 'Well-test water cut at standard conditions.', { suffix: 'wellTestWaterCutPts2' }),
  col('gor', 'gasOilRatio', 'Well-test gas-oil ratio at standard conditions.', { suffix: 'wellTestGorPts2' }),
];

/**
 * PTS well-test rates at the PTS REFERENCE conditions (the `PtsRef` sample) on
 * `PtsOutputs_String_Agent`. Trailing `…PtsRef` marker absorbed into
 * each `suffix`; applied with the default empty prefix. This sample carries the
 * condensate-gas ratio and the free GOR/CGR references (`wellTestCGRPtsRef`,
 * `freeGORPtsRef`, `freeCGRPtsRef`) which the other samples lack.
 */
export const PTS_RATES_REF: Shape = [
  col('oilRate', 'liquidRate', 'Well-test oil rate at PTS reference conditions.', { suffix: 'wellTestOilRatePtsRef' }),
  col('liquidRate', 'liquidRate', 'Well-test liquid rate at PTS reference conditions.', {
    suffix: 'wellTestLiquidRatePtsRef',
  }),
  col('waterCut', 'fraction', 'Well-test water cut at PTS reference conditions.', { suffix: 'wellTestWaterCutPtsRef' }),
  col('gor', 'gasOilRatio', 'Well-test gas-oil ratio at PTS reference conditions.', { suffix: 'wellTestGorPtsRef' }),
  col('cgr', 'liquidGasRatio', 'Well-test condensate-gas ratio at PTS reference conditions.', {
    suffix: 'wellTestCGRPtsRef',
  }),
  col('freeGor', 'gasOilRatio', 'Free gas-oil ratio at PTS reference conditions.', { suffix: 'freeGORPtsRef' }),
  col('freeCgr', 'liquidGasRatio', 'Free condensate-gas ratio at PTS reference conditions.', {
    suffix: 'freeCGRPtsRef',
  }),
];

/**
 * PTS fluid properties at LINE conditions (the `Pts1` sample) on
 * `PtsOutputs_String_Agent`: phase densities, gas specific gravity,
 * formation volume factors, and the fluid composition. Trailing `…Pts1` marker
 * absorbed into each `suffix`; applied with the default empty prefix.
 */
export const PTS_FLUID_PROPERTIES_PTS1: Shape = [
  col('oilDensity', 'unitless', 'Oil density at line conditions.', { suffix: 'oilDensityPts1' }),
  col('gasDensity', 'unitless', 'Gas density at line conditions.', { suffix: 'gasDensityPts1' }),
  col('waterDensity', 'unitless', 'Water density at line conditions.', { suffix: 'waterDensityPts1' }),
  col('specificGravityGas', 'unitless', 'Gas specific gravity at line conditions.', {
    suffix: 'specificGravityGasPts1',
  }),
  col('bo', 'unitless', 'Oil formation volume factor (Bo) at line conditions.', { suffix: 'boPts1' }),
  col('bg', 'unitless', 'Gas formation volume factor (Bg) at line conditions.', { suffix: 'bgPts1' }),
  col('fluidComposition', 'unitless', 'Fluid composition at line conditions.', { suffix: 'fluidCompositionPts1' }),
];

/**
 * PTS fluid properties at STANDARD conditions (the `Pts2` sample) on
 * `PtsOutputs_String_Agent`: phase densities, gas specific gravity, and
 * formation volume factors. The standard-conditions sample carries no fluid
 * composition column. Trailing `…Pts2` marker absorbed into each `suffix`;
 * applied with the default empty prefix.
 */
export const PTS_FLUID_PROPERTIES_PTS2: Shape = [
  col('oilDensity', 'unitless', 'Oil density at standard conditions.', { suffix: 'oilDensityPts2' }),
  col('gasDensity', 'unitless', 'Gas density at standard conditions.', { suffix: 'gasDensityPts2' }),
  col('waterDensity', 'unitless', 'Water density at standard conditions.', { suffix: 'waterDensityPts2' }),
  col('specificGravityGas', 'unitless', 'Gas specific gravity at standard conditions.', {
    suffix: 'specificGravityGasPts2',
  }),
  col('bo', 'unitless', 'Oil formation volume factor (Bo) at standard conditions.', { suffix: 'boPts2' }),
  col('bg', 'unitless', 'Gas formation volume factor (Bg) at standard conditions.', { suffix: 'bgPts2' }),
];

/**
 * Un-sampled PTS fluid composition + sample metadata on
 * `PtsOutputs_String_Agent`: mol fractions (CO2/H2S/N2), gas gravity,
 * water salinity, the raw composition + saturation pressure, the un-sampled
 * solution GOR/CGR, and the sample timestamp. These exist as bare source
 * columns (no `Pts*` marker), authored verbatim with `prefix:''` (suffix ===
 * source column). The mol-fraction columns are spelled `cO2` / `h2S` / `n2` at
 * the source (lowercase-leading), so the suffixes match that exact casing.
 */
export const PTS_FLUID_COMPOSITION: Shape = [
  col('co2', 'fraction', 'Carbon-dioxide mol fraction.', { suffix: 'cO2' }),
  col('h2s', 'fraction', 'Hydrogen-sulfide mol fraction.', { suffix: 'h2S' }),
  col('n2', 'fraction', 'Nitrogen mol fraction.'),
  col('gasGravity', 'unitless', 'Gas gravity.'),
  col('waterSalinity', 'unitless', 'Water salinity.'),
  col('composition', 'unitless', 'Raw fluid composition.'),
  col('saturationPressure', 'pressureAbsolute', 'Fluid saturation (bubble-point) pressure.'),
  col('solutionGor', 'gasOilRatio', 'Solution gas-oil ratio.', { suffix: 'solutionGOR' }),
  col('solutionCgr', 'liquidGasRatio', 'Solution condensate-gas ratio.', { suffix: 'solutionCGR' }),
  col('sampleDateTime', 'unitless', 'Timestamp of the PTS fluid sample.'),
];

/**
 * Un-sampled calculated / context PTS measures on `PtsOutputs_String_Agent`
 * The calculated and bare well-test GOR variants, the SC free GOR, and
 * the estimated / used gas-lift injection rates. These exist as bare source
 * columns (no `Pts*` marker), authored verbatim with `prefix:''` (suffix ===
 * source column). Source casing is irregular — the calculated GOR columns are
 * all-caps `…GOR`, the bare well-test GOR is `wellTestGOR`.
 */
export const PTS_CALC_CONTEXT: Shape = [
  col('calcGor', 'gasOilRatio', 'Calculated well-test gas-oil ratio.', { suffix: 'calcWellTestGOR' }),
  col('calcGorInSitu', 'gasOilRatio', 'Calculated in-situ well-test gas-oil ratio.', {
    suffix: 'calcWellTestGORInSitu',
  }),
  col('calcTotalGor', 'gasOilRatio', 'Calculated total well-test gas-oil ratio.', { suffix: 'calcWellTestTotalGOR' }),
  col('freeGorSc', 'gasOilRatio', 'Free gas-oil ratio at standard conditions.', { suffix: 'freeGORSC' }),
  col('wellTestGor', 'unitless', 'Well-test gas-oil ratio.', { suffix: 'wellTestGOR' }),
  col('estGasLiftInjectionRate', 'gasRate', 'Estimated gas-lift injection rate.'),
  col('usedGasLiftInjectionRate', 'gasRate', 'Used gas-lift injection rate.'),
  col('estimatedGasLiftRate', 'gasRate', 'Well-test estimated gas-lift rate.', { suffix: 'wellTestEstimatedGLRate' }),
  col('usedGasLiftRate', 'gasRate', 'Well-test used gas-lift rate.', { suffix: 'wellTestUsedGLRate' }),
];

/**
 * The network-grain relation carried by every `NetworkInputs_Agent` reshape
 * view: the direct-relation to the Network model entity. Kept verbatim
 * as the grain ref (the source already names it `networkName`), so the agent
 * can scope by / join the network. Authored with `suffix === canonical`
 * (passthrough, like `IDENTITY`/`STRING_RELATION`) — the Agent source carries
 * no prefix, so `prefix:''` yields `source === canonical` and no rename.
 */
